import lodash from 'lodash';
import {
    ValueSets,
    Operators,
} from '@complion/query';

function getValueSetDataHandlerProperty(valuesetName) {
    switch (valuesetName) {
        case ValueSets.ValueList.name:
            return 'values';
        case ValueSets.DataSourceMultipleValue.name:
            return 'selectedValues';
        case ValueSets.MultiselectList.name:
            return 'selectedValues';
        case ValueSets.SelectList.name:
            return 'selected';
        case ValueSets.DatasetSingleValue.name:
            return 'selected';
        case ValueSets.DatasetMultiselectList.name:
            return 'selectedValues';
        case ValueSets.DatasetValueList.name:
            return 'values';
        default:
            return 'value';
    }
}

function getValueSetAvailableValuesProperty(valuesetName) {
    switch (valuesetName) {
        case ValueSets.SelectList.name:
            return 'values';
        case ValueSets.MultiselectList.name:
            return 'values';
        case ValueSets.DatasetSingleValue.name:
            return 'values';
        case ValueSets.DatasetMultiselectList.name:
            return 'values';
        default:
            return null;
    }
}

class PredicateField {
    constructor(predicateGroupKey, value, errorMessages) {
        this.predicateGroupKey = predicateGroupKey;
        // lodash getter is used here so that value can be an empty string but not undefined
        this[this.dataHandlerProperty] = lodash.get({ value }, 'value', this.defaultValue);
        this._errorMessages = errorMessages;
    }

    get groupId() {
        return this.predicateGroupKey.groupId;
    }

    get dataType() {
        const { dataType } = this.predicateGroupKey;
        return typeof dataType === 'string' ? dataType.toLowerCase() : null;
    }

    get valueSetName() {
        return lodash.get(this.predicateGroupKey, 'value.type');
    }

    get operator() {
        return lodash.get(this.predicateGroupKey, 'operator');
    }

    get operatorName() {
        const operator = Operators.all[this.operator] || {};
        return operator.name;
    }

    get defaultValue() {
        return lodash.get(this.predicateGroupKey, `value.data.${this.dataHandlerProperty}`);
    }

    get dataHandlerProperty() {
        return getValueSetDataHandlerProperty(this.valueSetName);
    }

    get availableValues() {
        const property = getValueSetAvailableValuesProperty(this.valueSetName);
        return lodash.get(this.predicateGroupKey, `value.data.${property}`);
    }

    get dataSourceId() {
        return lodash.get(this.predicateGroupKey, 'value.dataSourceId');
    }

    get errorMessages() {
        return this._errorMessages;
    }

    getLabel(descriptor) {
        if (descriptor) {
            return isWhitespaceOrEmpty(descriptor.label)
                ? lodash.get(descriptor, 'displayName', 'Unnamed Descriptor')
                : descriptor.label;
        }
        const { type } = this.predicateGroupKey; //e.g. 'UploadDatePredicate'
        return [
            lodash.startCase,
            removePredicateSuffix,
            addSpacesBeforeCapitalLetters,
        ].reduce((result, modifier) => modifier(result), type); // e.g. 'Upload Date'
    }

    getLabelV4(descriptor) {
        if (descriptor) {
            return descriptor.label || 'Unnamed Descriptor'; 
        }
        const { type } = this.predicateGroupKey; 
        return [
            lodash.startCase,
            removePredicateSuffix,
            addSpacesBeforeCapitalLetters,
        ].reduce((result, modifier) => modifier(result), type); 
    }
    
    getDisplayName(descriptor) {
        if (descriptor) {
            return descriptor.displayName || 'Unnamed Descriptor'; 
        }
        const { type } = this.predicateGroupKey; 
        return [
            lodash.startCase,
            removePredicateSuffix,
            addSpacesBeforeCapitalLetters,
        ].reduce((result, modifier) => modifier(result), type);
    }
    

    getInstructionalText(descriptor) {
        if (descriptor && !isWhitespaceOrEmpty(descriptor.instructions)) {
            return descriptor.instructions;
        }
        const { operator, valueSetName, dataType } = this;
        return getInstructionalText(operator, valueSetName, dataType);
    }

    getDescriptorsThatShouldHaveAllValuesSelected() {
        const { operator, valueSetName, dataType } = this;
        return getDescriptorsThatShouldHaveAllValuesSelected(operator, valueSetName, dataType);
    }
}

function isWhitespaceOrEmpty(value) {
    if (lodash.isNil(value)) {
        return true;
    }
    if (!lodash.isString(value)) {
        return false;
    }
    return lodash.isEmpty(value.trim());
}

const PredicateRegexp = /Predicate$/;
const UpperCaseSpaceReplaceRegexp = /([a-z])([A-Z])/g;

/**
 * Removes the suffix of Predicate if it appears in a word.
 * @returns {*}
 * @example removePredicateSuffix('UploadDatePredicate') => 'UploadDate'
 */
function removePredicateSuffix(word) {
    return word.replace(PredicateRegexp, '');
}

/**
 * Adds a space before each capital letter that is immediately preceded by
 * a lower case letter
 * @param pascalCaseWord
 * @returns {string}
 * @example addSpacesBeforeCapitalLetters('UploadDate') => 'Upload Date'
 */
function addSpacesBeforeCapitalLetters(pascalCaseWord) {
    return pascalCaseWord.replace(UpperCaseSpaceReplaceRegexp, '$1 $2');
}

function makeSpecificInstructionKey(...args) {
    return args.filter(x => x).join('-');
}

// Use this to manage 
const specificInstructionMap = new Map([[
    makeSpecificInstructionKey(Operators.all.$eq.value, ValueSets.DatasetSingleValue.name),
    'Select one value from the list',
], [
    makeSpecificInstructionKey(Operators.all.$in.value, ValueSets.DataSourceMultipleValue.name),
    'Select one or multiple values from the list',
], [
    makeSpecificInstructionKey(Operators.all.$gte.value, ValueSets.ScalarValue.name, 'dateonly'),
    'Select a date',
], [
    makeSpecificInstructionKey(Operators.all.$gte.value, ValueSets.ScalarValue.name, 'datetime'),
    'Select a date',
], [
    makeSpecificInstructionKey(Operators.all.$in.value, ValueSets.DataSourceValueList.name),
    'All values from the list will be included',
], [
    makeSpecificInstructionKey(Operators.all.$gte.value, ValueSets.FixedValue.name, 'dateonly'),
    'Documents after this date will be included',
], [
    makeSpecificInstructionKey(Operators.all.$gte.value, ValueSets.FixedValue.name, 'datetime'),
    'Documents after this date will be included',
]]);

const descriptorsThatShouldHaveAllValuesSelected = new Map([[
    makeSpecificInstructionKey(Operators.all.$in.value, ValueSets.DataSourceValueList.name),
    true,
]]);

function getDescriptorsThatShouldHaveAllValuesSelected(operator, valueSetName, dataType) {
    return descriptorsThatShouldHaveAllValuesSelected.get(makeSpecificInstructionKey(operator, valueSetName, dataType)) ||
        descriptorsThatShouldHaveAllValuesSelected.get(makeSpecificInstructionKey(operator, valueSetName)) ||
        false;
}
function getInstructionalText(operator, valueSetName, dataType) {
    return specificInstructionMap.get(makeSpecificInstructionKey(operator, valueSetName, dataType)) ||
        specificInstructionMap.get(makeSpecificInstructionKey(operator, valueSetName)) ||
        'Select a value';
}

export default PredicateField;
